import React, { ReactNode, useEffect, useMemo, useState } from "react"
import { formatPhoneNumber } from "../../../shared/utils"
import { PublicLocation } from "../../../../domain/Profile/schema"
import { LocationPhoto } from "./LocationMap"
import { useSlideOver } from "../../../useSlideOver"
import { fetchLocationFullPhoto } from "../../../../domain/Profile/model"
import { DataStatus } from "../../../../domain/enums"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import { getLocationAddresses } from "./helpers"
import PlaceHolder from "../../../../assets/PlaceholderImage.png"
import RowContainer from "../../../shared/components/Layout/RowContainer"
import ColumnContainer from "../../../shared/components/Layout/ColumnContainer"
import {
  Typography,
  typographyTypes,
} from "../../../shared/components/Typography"
import { ButtonWrapper } from "../../../shared/components/ButtonWrapper"
import DividerBar from "../../../shared/components/DividerBar"
import Thumbnail from "../../../shared/components/Thumbnail"

interface LocationListItemProps {
  location: PublicLocation
  locations: PublicLocation[]
  selectedMarkerId: string | null
  showDivider: boolean
  onClick: () => void
  thumbnail: LocationPhoto
  geocodeLocationErrorIds: string[]
}

export const LIST_ITEM_ID_PREFIX = "location-item-"

export const LocationListIem: React.FC<LocationListItemProps> = ({
  location,
  locations,
  selectedMarkerId,
  showDivider,
  onClick,
  thumbnail,
  geocodeLocationErrorIds,
}) => {
  const { cms: cmsContent } = useCMSReady(["common", "publicProfileSlide"])
  const commonText = cmsContent.common
  const publicProfileText = cmsContent.publicProfileSlide

  const VIRTUAL = "virtual"
  const IMAGE_CAROUSEL = "imageCarousel"
  const ENTER = "Enter"
  const BUTTON = "button"

  const panelStyleOverrides =
    "min-w-[862px] min-h-[664px] p-3 sm:p-3 pb-16 sm:pb-16"
  const titleStyleOverrides = "text-[18px] font-medium ml-5"

  const locationAddresses = getLocationAddresses(location)
  const locationAddress = locationAddresses?.address
  const fullCityStateZip = locationAddresses?.fullCityStateZip
  const isVirtual = location.locationTypeDisplayId === VIRTUAL

  const { handleSetShowProfileModal } = useSlideOver()
  const [carouselLocationId, setCarouselLocationId] = useState<string>()
  const [locationFullImages, setLocationFullImages] = useState<LocationPhoto[]>(
    [],
  )
  const [fullImageDataStatus, setFullImageDataStatus] = useState<DataStatus>(
    DataStatus.LOADING,
  )
  const imageCarouselText = useMemo(
    () => ({
      imageAriaLabel: commonText?.thumbnail || "",
      previousButton: commonText?.previous || "",
      nextButton: commonText?.next || "",
    }),
    [commonText?.next, commonText?.previous, commonText?.thumbnail],
  )
  const locationHasGeocodeError = geocodeLocationErrorIds.includes(location.id)
  const locationType = location.isPrimary
    ? publicProfileText?.locationPrimaryWithColon
    : publicProfileText?.locationAlternate

  const onMultiThumbnailClick = (location: PublicLocation) => {
    // fetch the full images using the location.photoIds
    const tempLocationPhotos: LocationPhoto[] = location.locationPhotos.map(
      (photo) => ({
        id: photo.id,
        name: photo.name,
        fileName: photo.fileName,
        imageUrl: "placeholder",
        description: photo.description,
        isLoading: true,
      }),
    )

    const tempCarouselProps = {
      locationPhotos: tempLocationPhotos,
      text: imageCarouselText,
    }
    handleSetShowProfileModal({
      modalComponentRegistryKey: {
        type: IMAGE_CAROUSEL,
        props: tempCarouselProps,
      },
      panelStyleOverrides,
      titleStyleOverrides,
    })
    setCarouselLocationId(location.id)
  }

  const getThumbnailComponent = (
    location: PublicLocation,
  ): ReactNode | undefined => {
    const numberOfPhotos = location.locationPhotos.length
    const isMultiplePhotos = numberOfPhotos > 1
    const bubbleIndicatorText = `+${numberOfPhotos - 1}`
    const defaultPhotoName = thumbnail.name || thumbnail.fileName

    let thumbnailComponent
    if (numberOfPhotos >= 1) {
      thumbnailComponent = (
        <Thumbnail
          id={location.id || ""}
          imageUrl={thumbnail.imageUrl}
          imageAltText={defaultPhotoName}
          ariaLabel={`${imageCarouselText.imageAriaLabel} - ${defaultPhotoName}`}
          isLoading={thumbnail.isLoading}
          onClick={() => onMultiThumbnailClick(location)}
          bubbleIndicator={
            isMultiplePhotos ? { text: bubbleIndicatorText } : undefined
          }
          styleOverrides={`w-[132px] h-[100px] ${isMultiplePhotos && !thumbnail.isLoading ? "cursor-pointer" : ""} `}
        />
      )
    }

    return thumbnailComponent
  }

  useEffect(() => {
    const fetchLocationFullImages = async (locationId: string) => {
      const selectedLocation = locations.find(
        (location) => location.id === locationId,
      )
      const selectedLocationPhotoIds =
        selectedLocation?.locationPhotos.map((photo) => photo.id) ?? []
      const locationFullPhotos: LocationPhoto[] = []
      for (const photoId of selectedLocationPhotoIds) {
        const encodedLocationPhotoString =
          (await fetchLocationFullPhoto(
            locationId,
            photoId,
            setFullImageDataStatus,
          )) ?? PlaceHolder
        const selectedLocationPhoto = selectedLocation?.locationPhotos.find(
          (photo) => photo.id === photoId,
        )
        // we dont want to fail all images if one call fails so for now, im just discarding on failure
        if (!!selectedLocationPhoto) {
          const locationPhotoRecordValue: LocationPhoto = {
            id: photoId,
            name: selectedLocationPhoto.name,
            fileName: selectedLocationPhoto.fileName,
            imageUrl: encodedLocationPhotoString,
            description: selectedLocationPhoto.description,
            isLoading: false,
          }
          locationFullPhotos.push(locationPhotoRecordValue)
        }
      }
      setLocationFullImages(locationFullPhotos)
      handleSetShowProfileModal({
        modalComponentRegistryKey: {
          type: IMAGE_CAROUSEL,
          onCancel: false,
          onSubmit: false,
          props: {
            locationPhotos: locationFullPhotos,
            text: imageCarouselText,
          },
        },
        panelStyleOverrides,
        titleStyleOverrides,
      })
      // reset state so it is primed for next carousel click
      setCarouselLocationId(undefined)
      setFullImageDataStatus(DataStatus.LOADING)
    }

    if (carouselLocationId && fullImageDataStatus === DataStatus.LOADING) {
      fetchLocationFullImages(carouselLocationId)
    }
  }, [
    carouselLocationId,
    fullImageDataStatus,
    handleSetShowProfileModal,
    imageCarouselText,
    locationFullImages,
    locations,
  ])

  return (
    <div
      id={`${LIST_ITEM_ID_PREFIX}${location.id}`}
      className="flex flex-col space-y-0 w-full justify-between box-border"
      key={location.id}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === ENTER) {
          onClick()
        }
      }}
      role={BUTTON}
      tabIndex={0}
    >
      {showDivider && <DividerBar styleOverrides="bg-gray-300 w-full m-0" />}
      <RowContainer
        styleOverrides={`w-full justify-between py-4 px-6 box-border cursor-pointer ${selectedMarkerId === location.id ? "bg-[#F4FBFD]" : ""}`}
      >
        <ColumnContainer styleOverrides="space-y-0 justify-start text-left">
          <Typography
            variant={typographyTypes.HEADER_STATUS}
            text={locationType}
            styleOverrides="text-base"
          />
          {locationHasGeocodeError && (
            <Typography
              variant={typographyTypes.TABLE}
              text={publicProfileText?.locationGeocodeError}
              styleOverrides="italic"
            />
          )}
          {locationAddress && (
            <Typography
              variant={typographyTypes.NORMAL}
              text={locationAddress}
              styleOverrides="underline text-link-blue"
            />
          )}
          {fullCityStateZip && (
            <Typography
              variant={typographyTypes.NORMAL}
              text={fullCityStateZip}
            />
          )}
          {isVirtual && (
            <Typography
              variant={typographyTypes.NORMAL}
              text={publicProfileText?.locationsOnlinePractice}
            />
          )}
          {location.phones.map((phone) => (
            <Typography
              variant={typographyTypes.NORMAL}
              text={formatPhoneNumber(phone.number, true)}
              key={phone.id}
            />
          ))}
          {location.fax && (
            <Typography
              variant={typographyTypes.NORMAL}
              text={formatPhoneNumber(location.fax, true)}
              key={location.fax}
            />
          )}
          {location.emails.map((email) => (
            <ButtonWrapper
              key={email.id}
              onClick={() => (window.location.href = `mailto:${email.address}`)}
              styleOverrides="p-0 underline decoration-link-blue bg-transparent"
            >
              <Typography
                variant={typographyTypes.NORMAL}
                text={email.address}
                key={email.id}
                styleOverrides="text-link-blue"
              />
            </ButtonWrapper>
          ))}
        </ColumnContainer>
        {getThumbnailComponent(location) &&
          location.locationTypeDisplayId !== VIRTUAL &&
          getThumbnailComponent(location)}
      </RowContainer>
    </div>
  )
}
